/**
 * Client-side logger for the Breuer Klimaattechniek website.
 *
 * Captures browser errors and provides a log API that sends entries
 * to the Next.js proxy (/api/logs). Uses priority-based flushing:
 *
 * - error  → flush immediately
 * - warn   → flush within 2 seconds
 * - info/debug → batch up to 10 seconds or 10 entries
 *
 * Uses navigator.sendBeacon on page hide for reliable delivery.
 * Never blocks the UI thread.
 */

import { parseUserAgent, classifyDeviceType } from './parse-user-agent';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
	app: string;
	level: LogLevel;
	message: string;
	context?: string;
}

/**
 * Baseline fields attached to every client log entry — page URL, browser/OS,
 * device type and viewport — without every call site needing to remember.
 * Without this, generic errors (window.onerror, unhandledrejection) can't be
 * traced back to a page or browser at all.
 */
function baseContext(): Record<string, unknown> {
	if (typeof window === 'undefined') return {};

	const { browser, os } = parseUserAgent(navigator.userAgent || '');

	return {
		url: window.location.pathname + window.location.search,
		browser,
		os,
		device_type: classifyDeviceType(window.innerWidth),
		viewport: `${window.innerWidth}x${window.innerHeight}`,
	};
}

// ── Configuration ───────────────────────────────────────────────

const APP_ID = 'website';
const LOG_ENDPOINT = '/api/logs';

const BATCH_SIZE = 10;
const FLUSH_INTERVAL_MS = 10_000;
const WARN_FLUSH_MS = 2_000;
const DEDUP_WINDOW_MS = 5 * 60 * 1000; // 5 minutes

// ── State ───────────────────────────────────────────────────────

const buffer: LogEntry[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let warnTimer: ReturnType<typeof setTimeout> | null = null;
const recentErrors = new Map<string, number>(); // message → timestamp
let initialized = false;

// ── Deduplication ───────────────────────────────────────────────

function isDuplicate(message: string): boolean {
	const now = Date.now();

	// Clean old entries
	for (const [key, timestamp] of recentErrors) {
		if (now - timestamp > DEDUP_WINDOW_MS) {
			recentErrors.delete(key);
		}
	}

	if (recentErrors.has(message)) {
		return true;
	}

	recentErrors.set(message, now);

	return false;
}

// ── Flush logic ─────────────────────────────────────────────────

function doFlush(): void {
	if (buffer.length === 0) return;

	const entries = buffer.splice(0);
	const body = JSON.stringify({ entries });

	try {
		// Use sendBeacon if available (works even during page unload)
		if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
			const sent = navigator.sendBeacon(LOG_ENDPOINT, new Blob([body], { type: 'application/json' }));

			if (!sent) {
				// Beacon failed (e.g., queue full), fall back to fetch
				fetchLogs(body);
			}
		} else {
			fetchLogs(body);
		}
	} catch {
		// Swallow — logging must never break the app
	}
}

function fetchLogs(body: string): void {
	try {
		fetch(LOG_ENDPOINT, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body,
			keepalive: true, // Allow request to outlive the page
		}).catch(() => {
			// Swallow — fire and forget
		});
	} catch {
		// Swallow
	}
}

function scheduleFlush(): void {
	if (flushTimer) return;
	flushTimer = setTimeout(() => {
		flushTimer = null;
		doFlush();
	}, FLUSH_INTERVAL_MS);
}

function scheduleWarnFlush(): void {
	if (warnTimer) return;
	warnTimer = setTimeout(() => {
		warnTimer = null;
		doFlush();
	}, WARN_FLUSH_MS);
}

function cancelTimers(): void {
	if (flushTimer) {
		clearTimeout(flushTimer);
		flushTimer = null;
	}
	if (warnTimer) {
		clearTimeout(warnTimer);
		warnTimer = null;
	}
}

// ── Public API ──────────────────────────────────────────────────

function log(level: LogLevel, message: string, context?: Record<string, unknown>): void {
	// Errors/warnings: check dedup
	if ((level === 'error' || level === 'warn') && isDuplicate(message)) {
		return;
	}

	const mergedContext = { ...baseContext(), ...context };

	const entry: LogEntry = {
		app: APP_ID,
		level,
		message: message.substring(0, 2000), // Cap message length
		context: JSON.stringify(mergedContext).substring(0, 5000),
	};

	buffer.push(entry);

	// Priority-based flush
	if (level === 'error') {
		cancelTimers();
		doFlush();
	} else if (level === 'warn') {
		scheduleWarnFlush();
	} else if (buffer.length >= BATCH_SIZE) {
		cancelTimers();
		doFlush();
	} else {
		scheduleFlush();
	}
}

// ── Global error handlers ───────────────────────────────────────

function onError(event: ErrorEvent): void {
	log('error', event.message || 'Uncaught error', {
		filename: event.filename,
		lineno: event.lineno,
		colno: event.colno,
		stack: event.error?.stack?.substring(0, 2000),
	});
}

function onUnhandledRejection(event: PromiseRejectionEvent): void {
	const reason = event.reason;
	const message = reason instanceof Error ? reason.message : String(reason);

	log('error', `Unhandled promise rejection: ${message}`, {
		stack: reason instanceof Error ? reason.stack?.substring(0, 2000) : undefined,
	});
}

function onVisibilityChange(): void {
	if (document.visibilityState === 'hidden') {
		// Flush remaining logs when user navigates away
		cancelTimers();
		doFlush();
	}
}

// ── Init / Cleanup ──────────────────────────────────────────────

function init(): () => void {
	if (typeof window === 'undefined' || initialized) {
		return () => {};
	}

	initialized = true;

	window.addEventListener('error', onError);
	window.addEventListener('unhandledrejection', onUnhandledRejection);
	document.addEventListener('visibilitychange', onVisibilityChange);

	return () => {
		initialized = false;
		window.removeEventListener('error', onError);
		window.removeEventListener('unhandledrejection', onUnhandledRejection);
		document.removeEventListener('visibilitychange', onVisibilityChange);
		cancelTimers();
		doFlush(); // Flush remaining on cleanup
	};
}

export const clientLogger = {
	debug: (message: string, context?: Record<string, unknown>) => log('debug', message, context),
	info: (message: string, context?: Record<string, unknown>) => log('info', message, context),
	warn: (message: string, context?: Record<string, unknown>) => log('warn', message, context),
	error: (message: string, context?: Record<string, unknown>) => log('error', message, context),
	init,
};
